// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// Patent App No. ZA 2026/04523 · Priority Date: 08 October 2025
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import axios from "axios";
import {
  Play,
  CheckCircle2,
  XCircle,
  Clock,
  Banknote,
  Building2,
  ArrowRight,
  RefreshCw,
  Copy,
  Zap,
  AlertCircle,
  ShieldCheck,
  ArrowDownToLine,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DENOMINATIONS = [200, 100, 50, 20, 10];

const formatCurrency = (a) =>
  new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 2,
  }).format(a || 0);

const AtmDeposit = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Step control
  const [step, setStep] = useState(1); // 1=account, 2=cash, 3=confirm, 4=done

  // Form
  const [acquirerBank, setAcquirerBank] = useState("");   // ATM owner
  const [receivingBank, setReceivingBank] = useState(""); // destination bank
  const [receivingAccount, setReceivingAccount] = useState("");
  const [depositRef, setDepositRef] = useState("");
  const [depositorName, setDepositorName] = useState("");
  const [depositorSurname, setDepositorSurname] = useState("");
  const [depositorMsisdn, setDepositorMsisdn] = useState("");
  const [atmId] = useState(`ATM-${Math.random().toString(36).substring(2, 8).toUpperCase()}`);

  // Denomination counters
  const [notes, setNotes] = useState({ 200: 0, 100: 0, 50: 0, 20: 0, 10: 0 });

  // Backend responses
  const [accountValidation, setAccountValidation] = useState(null);
  const [initResp, setInitResp] = useState(null);
  const [confirmResp, setConfirmResp] = useState(null);
  const [flowLog, setFlowLog] = useState([]);

  const total = Object.entries(notes).reduce(
    (sum, [v, c]) => sum + parseInt(v) * parseInt(c || 0),
    0
  );

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("360wallet_token");
        const res = await axios.get(`${API_URL}/api/banks`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setBanks((res.data || []).filter((b) => b.is_active !== false));
      } catch (e) {
        toast.error("Failed to load banks");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const addLog = (step, status, details) =>
    setFlowLog((prev) => [
      ...prev,
      { ts: new Date().toISOString(), step, status, details },
    ]);

  const validateAccount = async () => {
    if (!acquirerBank || !receivingBank || !receivingAccount) {
      toast.error("Fill ATM bank, destination bank and account number");
      return;
    }
    if (!depositorName || !depositorSurname || !depositorMsisdn) {
      toast.error("Fill depositor name, surname and cellphone");
      return;
    }
    const token = localStorage.getItem("360wallet_token");
    addLog("1. VALIDATE ACCOUNT", "PENDING", `Verifying ${receivingBank} account ${receivingAccount}`);
    try {
      const res = await axios.post(
        `${API_URL}/api/simulator/deposit/validate-account`,
        {
          atm_id: atmId,
          acquirer_bank_code: acquirerBank,
          receiving_bank_code: receivingBank,
          receiving_account_number: receivingAccount,
          atm_transaction_id: `DEP-${Date.now()}`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAccountValidation(res.data);
      if (res.data.account_valid) {
        addLog("1. ACCOUNT VALIDATED", "SUCCESS", `Holder: ${res.data.account_holder_name_masked} at ${res.data.receiving_bank_name}`);
        setStep(2);
      } else {
        addLog("1. ACCOUNT INVALID", "FAILED", res.data.decline_reason || "Unknown");
        toast.error(res.data.decline_reason || "Account invalid");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      addLog("1. ACCOUNT ERROR", "FAILED", msg);
      toast.error(msg);
    }
  };

  const submitCashCount = async () => {
    if (total <= 0) {
      toast.error("Insert at least one note");
      return;
    }
    const denominations = Object.entries(notes)
      .filter(([, c]) => c > 0)
      .map(([v, c]) => ({ value: parseInt(v), count: parseInt(c) }));

    const token = localStorage.getItem("360wallet_token");
    addLog("2. CASH ACCEPTED", "PENDING", `Total ${formatCurrency(total)} in ${denominations.reduce((a,b)=>a+b.count,0)} notes`);
    try {
      const res = await axios.post(
        `${API_URL}/api/simulator/deposit/initiate`,
        {
          atm_id: atmId,
          atm_transaction_id: `DEP-${Date.now()}`,
          acquirer_bank_code: acquirerBank,
          receiving_bank_code: receivingBank,
          receiving_account_number: receivingAccount,
          receiving_account_holder_masked: accountValidation?.account_holder_name_masked,
          deposit_reference: depositRef,
          depositor_name: depositorName,
          depositor_surname: depositorSurname,
          depositor_msisdn: depositorMsisdn,
          deposit_amount: total,
          denominations,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInitResp(res.data);
      if (res.data.deposit_status === "AWAITING_CONFIRMATION") {
        addLog("2. NOTES VERIFIED", "SUCCESS", `Limit scope: ${res.data.limit_scope_applied}`);
        addLog("3. AWAITING USER CONFIRMATION", "PENDING", "Deposit token issued (120s)");
        setStep(3);
      } else {
        addLog("2. DECLINED", "FAILED", res.data.decline_reason);
        toast.error(res.data.decline_reason || "Declined");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      addLog("2. CASH ERROR", "FAILED", msg);
      toast.error(msg);
    }
  };

  const confirm = async (confirmed) => {
    const token = localStorage.getItem("360wallet_token");
    addLog(confirmed ? "3. USER CONFIRMED" : "3. USER CANCELLED", confirmed ? "PENDING" : "FAILED",
      confirmed ? "Clearing to receiving bank in real time" : "Cash will be returned");
    try {
      const res = await axios.post(
        `${API_URL}/api/simulator/deposit/confirm`,
        {
          deposit_id: initResp.deposit_id,
          deposit_token: initResp.deposit_token,
          user_confirmed: confirmed,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConfirmResp(res.data);
      if (res.data.final_status === "COMPLETED") {
        addLog("4. CLEARED", "SUCCESS", `Credit posted to ${receivingBank}. Fee ${formatCurrency(res.data.fee_applied)}`);
        addLog("5. RECEIPT SENT", "SUCCESS", `SMS to ${depositorMsisdn}`);
        toast.success(`Deposit ${formatCurrency(total)} cleared to ${receivingBank}!`);
      } else {
        addLog("4. CANCELLED", "FAILED", "Cash returned");
        toast.info("Deposit cancelled — cash returned");
      }
      setStep(4);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      addLog("3. CLEARING ERROR", "FAILED", msg);
      toast.error(msg);
      setStep(4);
    }
  };

  const reset = () => {
    setStep(1);
    setReceivingAccount("");
    setDepositRef("");
    setDepositorName("");
    setDepositorSurname("");
    setDepositorMsisdn("");
    setNotes({ 200: 0, 100: 0, 50: 0, 20: 0, 10: 0 });
    setAccountValidation(null);
    setInitResp(null);
    setConfirmResp(null);
    setFlowLog([]);
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="atm-deposit-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2">
            <ArrowDownToLine className="h-6 w-6 text-primary" />
            Cross-Bank ATM Cash Deposit
          </h1>
          <p className="text-muted-foreground">
            Deposit cash at any ATM · Real-time clearing to any participating bank
          </p>
        </div>
        <Button variant="outline" onClick={reset} data-testid="reset-deposit-btn">
          <RefreshCw className="h-4 w-4 mr-2" /> Reset
        </Button>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { n: 1, label: "Account & Depositor" },
          { n: 2, label: "Insert Cash" },
          { n: 3, label: "Confirm" },
          { n: 4, label: "Receipt" },
        ].map((s) => (
          <div key={s.n} className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border ${
                step === s.n
                  ? "bg-primary text-primary-foreground border-primary"
                  : step > s.n
                  ? "bg-green-500/20 text-green-400 border-green-500/30"
                  : "bg-secondary text-muted-foreground border-border"
              }`}
            >
              {step > s.n ? <CheckCircle2 className="h-4 w-4" /> : s.n}
            </div>
            <span className={`text-sm ${step === s.n ? "font-bold" : "text-muted-foreground"}`}>
              {s.label}
            </span>
            {s.n < 4 && <ArrowRight className="h-4 w-4 text-muted-foreground" />}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT PANEL: input */}
        <Card className="lg:col-span-1 bg-card border-border">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              {step === 1 && "Step 1: Depositor & Account"}
              {step === 2 && "Step 2: Insert Cash"}
              {step === 3 && "Step 3: Confirm on Screen"}
              {step === 4 && "Step 4: Receipt"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {step === 1 && (
              <>
                <div className="space-y-2">
                  <Label>ATM Bank (Acquirer)</Label>
                  <Select value={acquirerBank} onValueChange={setAcquirerBank}>
                    <SelectTrigger data-testid="acquirer-bank-select">
                      <SelectValue placeholder="Bank owning the ATM" />
                    </SelectTrigger>
                    <SelectContent>
                      {banks.map((b) => (
                        <SelectItem key={b.bank_code} value={b.bank_code}>
                          {b.bank_code} — {b.bank_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Receiving Bank (Issuer)</Label>
                  <Select value={receivingBank} onValueChange={setReceivingBank}>
                    <SelectTrigger data-testid="receiving-bank-select">
                      <SelectValue placeholder="Bank of destination account" />
                    </SelectTrigger>
                    <SelectContent>
                      {banks.map((b) => (
                        <SelectItem key={b.bank_code} value={b.bank_code}>
                          {b.bank_code} — {b.bank_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Destination Account Number</Label>
                  <Input
                    value={receivingAccount}
                    onChange={(e) => setReceivingAccount(e.target.value.replace(/\D/g, ""))}
                    placeholder="9012345678"
                    maxLength={13}
                    data-testid="account-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Deposit Reference (optional)</Label>
                  <Input
                    value={depositRef}
                    onChange={(e) => setDepositRef(e.target.value)}
                    placeholder="Rent August"
                    maxLength={64}
                    data-testid="deposit-ref-input"
                  />
                </div>
                <Separator />
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label>Depositor Name</Label>
                    <Input value={depositorName} onChange={(e) => setDepositorName(e.target.value)} placeholder="Thabo" data-testid="depositor-name-input"/>
                  </div>
                  <div className="space-y-2">
                    <Label>Surname</Label>
                    <Input value={depositorSurname} onChange={(e) => setDepositorSurname(e.target.value)} placeholder="Mokoena" data-testid="depositor-surname-input"/>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Cellphone (SA)</Label>
                  <Input
                    value={depositorMsisdn}
                    onChange={(e) => setDepositorMsisdn(e.target.value)}
                    placeholder="0821234567"
                    data-testid="depositor-msisdn-input"
                  />
                </div>
                <Button
                  className="w-full shadow-[0_0_15px_rgba(59,130,246,0.3)]"
                  onClick={validateAccount}
                  data-testid="validate-account-btn"
                >
                  <Play className="h-4 w-4 mr-2" /> Validate Account
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                {accountValidation && (
                  <div className="p-3 rounded-md bg-green-500/10 border border-green-500/30">
                    <div className="text-xs text-muted-foreground">Confirmed destination</div>
                    <div className="font-bold">{accountValidation.account_holder_name_masked}</div>
                    <div className="text-sm text-muted-foreground">
                      {accountValidation.receiving_bank_name} · {receivingAccount}
                    </div>
                  </div>
                )}
                <Label>Insert Notes (simulated ATM note-counter)</Label>
                <div className="space-y-2">
                  {DENOMINATIONS.map((d) => (
                    <div key={d} className="flex items-center gap-2">
                      <Badge variant="outline" className="w-16 justify-center">R{d}</Badge>
                      <Input
                        type="number"
                        min="0"
                        value={notes[d]}
                        onChange={(e) =>
                          setNotes({ ...notes, [d]: parseInt(e.target.value || 0) })
                        }
                        className="flex-1"
                        data-testid={`notes-${d}-input`}
                      />
                      <span className="text-sm text-muted-foreground w-20 text-right">
                        {formatCurrency(d * notes[d])}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="p-3 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-between">
                  <span className="text-sm font-medium">Total Deposit</span>
                  <span className="font-mono font-bold text-lg text-primary" data-testid="total-amount">
                    {formatCurrency(total)}
                  </span>
                </div>
                <Button
                  className="w-full"
                  onClick={submitCashCount}
                  disabled={total <= 0}
                  data-testid="submit-cash-btn"
                >
                  <Banknote className="h-4 w-4 mr-2" /> Confirm Note Count
                </Button>
              </>
            )}

            {step === 3 && initResp && (
              <>
                <div className="p-4 rounded-md bg-secondary/50 border border-border">
                  <div className="text-xs text-muted-foreground mb-2">Please confirm</div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Total deposit</span>
                      <span className="font-mono font-bold">{formatCurrency(initResp.total_amount)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>To</span>
                      <span>{accountValidation?.account_holder_name_masked}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Bank</span>
                      <span>{accountValidation?.receiving_bank_name}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Account</span>
                      <span className="font-mono">{receivingAccount}</span>
                    </div>
                    <Separator />
                    <div className="text-xs text-muted-foreground">Note breakdown</div>
                    {initResp.denomination_breakdown?.map((d) => (
                      <div key={d.value} className="flex justify-between text-xs">
                        <span>R{d.value} × {d.count}</span>
                        <span className="font-mono">{formatCurrency(d.value * d.count)}</span>
                      </div>
                    ))}
                    {initResp.requires_edd && (
                      <div className="mt-2 p-2 rounded bg-yellow-500/10 border border-yellow-500/30 text-xs flex items-center gap-1">
                        <ShieldCheck className="h-3 w-3 text-yellow-400" />
                        Enhanced Due Diligence flagged (above threshold)
                      </div>
                    )}
                    <div className="mt-2 text-xs text-muted-foreground">
                      Limit scope: <code className="font-mono">{initResp.limit_scope_applied}</code>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={() => confirm(false)} data-testid="cancel-btn">
                    <XCircle className="h-4 w-4 mr-2" /> Cancel
                  </Button>
                  <Button onClick={() => confirm(true)} data-testid="confirm-btn">
                    <CheckCircle2 className="h-4 w-4 mr-2" /> Confirm
                  </Button>
                </div>
              </>
            )}

            {step === 4 && confirmResp && (
              <div className="text-center space-y-3">
                {confirmResp.final_status === "COMPLETED" ? (
                  <>
                    <CheckCircle2 className="h-16 w-16 text-green-400 mx-auto" />
                    <h3 className="font-heading font-bold text-xl">Deposit Successful</h3>
                    <p className="text-sm text-muted-foreground">
                      Receipt sent to <span className="font-mono">{depositorMsisdn}</span>
                    </p>
                  </>
                ) : (
                  <>
                    <XCircle className="h-16 w-16 text-red-400 mx-auto" />
                    <h3 className="font-heading font-bold text-xl">Deposit Cancelled</h3>
                    <p className="text-sm text-muted-foreground">Cash returned. No fee.</p>
                  </>
                )}
                <Button onClick={reset} className="w-full">
                  <RefreshCw className="h-4 w-4 mr-2" /> New Deposit
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* RIGHT PANEL: flow log */}
        <Card className="lg:col-span-2 bg-card border-border">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Transaction Flow</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Route visualization */}
            {acquirerBank && receivingBank && (
              <div className="p-4 rounded-lg bg-secondary/30 border border-border">
                <div className="flex items-center justify-center gap-4 flex-wrap">
                  <div className="text-center">
                    <div className="p-3 rounded-full bg-cyan-500/20 inline-block">
                      <Banknote className="h-6 w-6 text-cyan-400" />
                    </div>
                    <p className="text-sm font-medium mt-2">{acquirerBank} ATM</p>
                    <p className="text-xs text-muted-foreground">Cash In</p>
                  </div>
                  <ArrowRight className="h-6 w-6 text-primary" />
                  <div className="text-center">
                    <div className="p-3 rounded-full bg-primary/20 inline-block">
                      <Zap className="h-6 w-6 text-primary" />
                    </div>
                    <p className="text-sm font-medium mt-2">360Wallet</p>
                    <p className="text-xs text-muted-foreground">Real-Time Router</p>
                  </div>
                  <ArrowRight className="h-6 w-6 text-primary" />
                  <div className="text-center">
                    <div className="p-3 rounded-full bg-blue-500/20 inline-block">
                      <Building2 className="h-6 w-6 text-blue-400" />
                    </div>
                    <p className="text-sm font-medium mt-2">{receivingBank}</p>
                    <p className="text-xs text-muted-foreground">Account Credited</p>
                  </div>
                </div>
              </div>
            )}

            {/* Flow log */}
            <div className="space-y-2">
              <h3 className="font-medium text-sm text-muted-foreground">Flow Log</h3>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {flowLog.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Zap className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Fill the form to start the deposit flow</p>
                  </div>
                ) : (
                  flowLog.map((l, i) => (
                    <div
                      key={i}
                      className={`p-3 rounded-md border ${
                        l.status === "SUCCESS"
                          ? "bg-green-500/5 border-green-500/20"
                          : l.status === "FAILED"
                          ? "bg-red-500/5 border-red-500/20"
                          : "bg-yellow-500/5 border-yellow-500/20"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          {l.status === "SUCCESS" && <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0" />}
                          {l.status === "FAILED" && <XCircle className="h-4 w-4 text-red-400 shrink-0" />}
                          {l.status === "PENDING" && <Clock className="h-4 w-4 text-yellow-400 shrink-0" />}
                          <span className="font-medium text-sm">{l.step}</span>
                        </div>
                        <span className="text-xs text-muted-foreground font-mono">
                          {new Date(l.ts).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 ml-6">{l.details}</p>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Final receipt */}
            {confirmResp?.final_status === "COMPLETED" && (
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                <div className="text-xs text-muted-foreground mb-2">RECEIPT</div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Deposit ID</span>
                    <span className="font-mono text-xs flex items-center gap-1">
                      {confirmResp.deposit_id.substring(0, 8)}...
                      <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copy(confirmResp.deposit_id)}>
                        <Copy className="h-3 w-3" />
                      </Button>
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Amount</span>
                    <span className="font-mono font-bold">{formatCurrency(total)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Fee</span>
                    <span className="font-mono">{formatCurrency(confirmResp.fee_applied)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Cleared at</span>
                    <span className="font-mono text-xs">{new Date(confirmResp.cleared_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AtmDeposit;
